/**
 * NMS Mega Menu Toggle Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Handles switching between "By Category" and "By Use" views
 *
 * @example
 * <!-- Automatic initialization via data-nms-mega-menu-toggle -->
 * <div data-nms-mega-menu-toggle data-menu-id="women">
 *   <button data-ref="toggleButtons[]" data-nms-click="/handleToggle/category" data-toggle-target="category">Category</button>
 *   <div data-ref="contentContainers[]" data-toggle-content="category">Content</div>
 * </div>
 */

class NMSMegaMenuToggle extends window.NMSBaseClass {
  #isInitialized = false;
  #activeType = 'category'; // Private field for current state

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.menuId = element.dataset.menuId || `menu-${Date.now()}`; // REQUIRED: Unique identifier

    // REQUIRED: Store in global registry for external access
    window.neemans.megaMenuToggle = window.neemans.megaMenuToggle || {};
    window.neemans.megaMenuToggle[this.menuId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init(); // This handles refs and event binding automatically
    this.#initializeMegaMenuState();
    this.#setInitialState();

    this.#isInitialized = true;
  }

  #initializeMegaMenuState() {
    // Set up global data in window.neemans namespace
    this.setGlobalData(`megaMenuToggle.${this.menuId}`, {
      activeType: this.#activeType,
      menuId: this.menuId,
    });
  }

  destroy() {
    // Clean up global reference
    if (window.neemans.megaMenuToggle?.[this.menuId]) {
      delete window.neemans.megaMenuToggle[this.menuId];
    }

    super.destroy();
    this.#isInitialized = false;
  }

  /**
   * Initialize component state based on DOM
   * Called once during component setup
   */
  #setInitialState() {
    // Find currently active button from DOM state
    const activeButton = this.refs.toggleButtons?.find(btn =>
      btn.classList.contains('nms-mega-menu__toggle-button--active')
    );

    this.#activeType = activeButton?.dataset.toggleTarget || 'category';

    // Ensure state consistency
    this.#syncState();
  }

  /**
   * Handle toggle button clicks via data-nms-click
   * @param {Event} event - Click event
   * @param {string} targetType - 'category' or 'use'
   */
  handleToggle(event, targetType) {
    event.preventDefault();

    // Skip if already active
    if (this.#activeType === targetType) return;

    // Update state
    this.#activeType = targetType;

    // Update global state
    this.setGlobalData(`megaMenuToggle.${this.menuId}.activeType`, targetType);

    // Update DOM
    this.#updateButtonStates(targetType);
    this.#switchContent(targetType);

  }

  /**
   * Update button visual states and ARIA attributes
   * @param {string} activeType - The type to make active
   */
  #updateButtonStates(activeType) {
    if (!this.refs.toggleButtons) return;

    for (const button of this.refs.toggleButtons) {
      const buttonType = button.dataset.toggleTarget;
      const isActive = buttonType === activeType;

      // Reset classes
      button.classList.remove('nms-mega-menu__toggle-button--active', 'nms-button--primary', 'nms-button--secondary');

      if (isActive) {
        // Active button: primary styling
        button.classList.add('nms-button--primary', 'nms-mega-menu__toggle-button--active');
        button.setAttribute('aria-pressed', 'true');
      } else {
        // Inactive button: secondary styling
        button.classList.add('nms-button--secondary');
        button.setAttribute('aria-pressed', 'false');
      }
    }
  }

  /**
   * Switch content visibility based on active type
   * @param {string} activeType - The content type to show
   */
  #switchContent(activeType) {
    if (!this.refs.contentContainers) return;

    for (const container of this.refs.contentContainers) {
      const containerType = container.dataset.toggleContent;
      const isVisible = containerType === activeType;

      if (isVisible) {
        container.style.display = '';
        container.setAttribute('aria-hidden', 'false');
      } else {
        container.style.display = 'none';
        container.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Sync internal state with actual DOM state
   * Used for state correction and initialization
   */
  #syncState() {
    this.#updateButtonStates(this.#activeType);
    this.#switchContent(this.#activeType);
  }

  /**
   * Public API: Get current active type
   * @returns {string} Current active type ('category' or 'use')
   */
  getActiveType() {
    return this.#activeType;
  }

  /**
   * Public API: Set active type programmatically
   * @param {string} type - Type to activate ('category' or 'use')
   */
  setActiveType(type) {
    if (type !== this.#activeType) {
      this.#activeType = type;

      // Update global state
      this.setGlobalData(`megaMenuToggle.${this.menuId}.activeType`, type);

      this.#syncState();

    }
  }

  /**
   * Reset to default state (category)
   * Useful when mega menu becomes visible
   */
  resetToDefault() {
    this.setActiveType('category');
  }

  /**
   * Get menu ID
   * @returns {string} Menu ID
   */
  getMenuId() {
    return this.menuId;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSMegaMenuToggle(container = document) {
  if (!window.NMSBaseClass) {
    console.error('NMSMegaMenuToggle: NMSBaseClass required');
    return;
  }

  // Find all mega menu toggle components
  const elements = container.querySelectorAll('[data-nms-mega-menu-toggle]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSMegaMenuToggle(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSMegaMenuToggle);
} else {
  initNMSMegaMenuToggle();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-mega-menu-toggle]')) {
    initNMSMegaMenuToggle(event.target);
  }
});

// Re-initialize when new content is added (for theme editor, AJAX, etc.)
document.addEventListener('nms:content-updated', event => {
  initNMSMegaMenuToggle(event.detail?.container || document);
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSMegaMenuToggle = NMSMegaMenuToggle;

// Global initialization function for other components to use
window.initNMSMegaMenuToggle = initNMSMegaMenuToggle;
